<template>
    <page-header>Terms &amp; Conditions</page-header>

    <terms-controls v-model="fetchParams" @apply="applyFilters" @reset="resetFilters">
        <vx-button size="lg" block :href="newTermHref" data-test="new-term-button">
            New Terms <font-awesome-icon widthAuto :icon="faCirclePlus" />
        </vx-button>
    </terms-controls>

    <page-loading v-if="loading && !list.length" />

    <template v-if="!loading && !list.length">
        <empty-state v-if="!filtersAreDefault" @reset="resetFilters" />
        <empty-state v-else>
            No Terms yet.
            <vx-button :href="newTermHref" data-test="empty-state-new-term-button">
                New Terms <font-awesome-icon widthAuto :icon="faCirclePlus" />
            </vx-button>
        </empty-state>
    </template>

    <vx-table v-if="list.length" class="xl:grid-cols-5-auto" rowBreakpoint="xl">
        <vx-table-row isHeader>
            <div>Title</div>
            <div>Status</div>
            <div>Effective At</div>
            <div>Creator</div>
        </vx-table-row>

        <term-row
            v-for="term in list"
            :key="term.id"
            :teamId="props.teamId"
            :term="term"
            @delete="destroy"
            @pause="pause"
            @resume="resume"
            @archive="archive"
        />
    </vx-table>

    <div v-if="list.length && fetchParams.cursor" class="mt-8 flex justify-between w-full gap-10">
        <vx-button
            @click="loadData()"
            color="muted-dark"
            size="xl"
            class="grow-0"
            :loading="loading"
            data-test="load-more-button"
        >
            Load More <font-awesome-icon widthAuto :icon="faSpinner" />
        </vx-button>
    </div>
</template>

<script lang="ts" setup>
import { VxButton, VxSwal, VxTable, VxTableRow } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCirclePlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { computed, onMounted } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import { adminTermsClient } from '~/services';
import type { Term, TermListParams, TermStatus } from '~/services/adminTermsClient.types';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { throwSwalRequestError } from '~/utils/request';
import TermsControls from './TermsControls.vue';
import TermRow from './TermRow.vue';

const props = defineProps<{
    teamId: number;
}>();

const { list, loading, fetchParams, fetchRecords } = useCursorPagination<Term, TermListParams>();

const newTermHref = computed(() => `/kiosk/teams/${props.teamId}/terms/new`);

const defaultFilters = () => ({
    per_page: 20,
    sort_field: 'id',
    sort_dir: 'desc' as const,
    cursor: null,
    query: '',
});

const filtersAreDefault = computed(() => !fetchParams.value.query && !fetchParams.value.status?.length);

const loadData = () => {
    fetchRecords(adminTermsClient.getTerms(props.teamId, fetchParams.value));
};

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    loadData();
};

onMounted(() => {
    resetFilters();
});

const destroy = (term: Term) => {
    VxSwal.fire({
        title: 'Delete Terms',
        text: 'Are you sure? There is no undo.',
        icon: 'danger',
        showCancelButton: true,
        confirmButtonColor: 'danger',
        preConfirm: () =>
            adminTermsClient
                .deleteTerm(props.teamId, term.id)
                .then(() => {
                    list.value = list.value.filter((item) => item.id !== term.id);
                    if (!list.value.length) {
                        resetFilters();
                    }
                })
                .catch(throwSwalRequestError),
    });
};

const changeStatus = (term: Term, status: Exclude<TermStatus, 'draft'>) =>
    adminTermsClient
        .updateTermStatus(props.teamId, term.id, status)
        .then(() => {
            list.value = list.value.map((item) => {
                if (item.id === term.id) {
                    return { ...item, status };
                }

                if (status === 'published' && (item.status === 'published' || item.status === 'paused')) {
                    return { ...item, status: 'archived' };
                }

                return item;
            });
        })
        .catch(throwSwalRequestError);

const pause = (term: Term) => {
    VxSwal.fire({
        title: 'Pause Terms',
        text: 'While paused, covered groups will not see the acceptance pop-up and prior acceptances are preserved. You can resume at any time.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'primary',
        preConfirm: () => changeStatus(term, 'paused'),
    });
};

const resume = (term: Term) => {
    VxSwal.fire({
        title: 'Resume Terms',
        text: 'These Terms will be enforced again. Groups without an acceptance from their current owner will be prompted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'primary',
        preConfirm: () => changeStatus(term, 'published'),
    });
};

const archive = (term: Term) => {
    VxSwal.fire({
        title: 'Archive Terms',
        text: 'Archived Terms are no longer enforced and cannot be reactivated. Acceptance history remains available for audit. There is no undo.',
        icon: 'danger',
        showCancelButton: true,
        confirmButtonColor: 'danger',
        preConfirm: () => changeStatus(term, 'archived'),
    });
};
</script>
